export const drumsSyllabus = {
  1: {
    id: 1,
    title: "Level 1 — Initial Basic (Foundation Level)",
    objective:
      "To introduce the drum kit, develop basic coordination, rhythmic awareness and understanding of time signatures.",
    assessmentFocus: [
      "Time stability",
      "Coordination",
      "Technique safety",
      "Groove consistency",
    ],
    examFormat: {
      title: "Exam Format (Level 1)",
      totalMarks: 100,
      components: [
        { criteria: "Time & Tempo", marks: 30 },
        { criteria: "Coordination", marks: 25 },
        { criteria: "Technique", marks: 20 },
        { criteria: "Groove Consistency", marks: 15 },
        { criteria: "Confidence & Control", marks: 10 },
      ],
      notes: "Notation policy is optional, counting system is mandatory (verbal or numeric).",
    },
    practicalSections: [
      {
        title: "Instrument fundamentals",
        items: [
          "Parts of the drum kit (bass drum, snare, toms, hi-hat, ride, crash)",
          "Correct seating position and posture",
          "Stick grip (matched grip) and basic stroke types (full, down, tap, up)",
        ],
      },
      {
        title: "Basic coordination and grooves",
        items: [
          "Simple right-hand hi-hat patterns with bass and snare combinations",
          "Basic rock beats in 4/4 time (two to three standard patterns)",
          "Playing along with a metronome or slow backing track",
        ],
      },
      {
        title: "Rudiments and technical work",
        items: [
          "Single-stroke roll (slow to moderate tempo)",
          "Double-stroke roll (slow tempo)",
          "Single paradiddle (basic familiarity)",
        ],
      },
      {
        title: "Repertoire and performance",
        items: [
          "One to two short performance pieces or studies using Level 1 grooves",
          "Simple 4-bar fill leading back into a groove",
        ],
      },
    ],
    theorySections: [
      {
        title: "Rhythm and notation basics",
        items: [
          "Note values: whole, half, quarter and eighth notes",
          "Basic rests and counting in 4/4",
          "Simple drum-kit notation (stave layout and instrument placement)",
        ],
      },
      {
        title: "Time and pulse",
        items: [
          "Understanding pulse and tempo",
          "Counting bars in 4/4 and 3/4",
        ],
      },
    ],
  },
  2: {
    id: 2,
    title: "Level 2 — Intermediate (Developing Control)",
    objective:
      "To strengthen coordination, expand rhythmic vocabulary and introduce new time signatures and dynamics.",
    assessmentFocus: [
      "Time stability",
      "Groove control",
      "Rudiment clarity",
      "Independence development",
    ],
    examFormat: {
      title: "Exam Format (Level 2)",
      totalMarks: 100,
      components: [
        { criteria: "Time & Tempo Control", marks: 25 },
        { criteria: "Groove Consistency", marks: 25 },
        { criteria: "Coordination & Independence", marks: 20 },
        { criteria: "Technique & Rudiments", marks: 20 },
        { criteria: "Musical Confidence", marks: 10 },
      ],
      notes: "Drum notation optional, counting, listening, and execution prioritized.",
    },
    practicalSections: [
      {
        title: "Coordination and grooves",
        items: [
          "Development of 4/4 rock patterns with varied bass drum placements",
          "Introduction to 3/4 and 6/8 grooves",
          "Hi-hat opening and closing within grooves",
        ],
      },
      {
        title: "Rudiments and fills",
        items: [
          "Improved single and double-stroke rolls",
          "Paradiddles at moderate tempo",
          "Simple tom fills using quarter and eighth notes",
        ],
      },
      {
        title: "Dynamic control and sound",
        items: [
          "Playing at different dynamic levels",
          "Basic accent patterns on snare and hi-hat",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Two short pieces demonstrating different grooves",
          "Basic drum part for a simple song or backing track",
        ],
      },
    ],
    theorySections: [
      {
        title: "Rhythm and notation",
        items: [
          "Expanded note values and rests (including dotted notes)",
          "Simple syncopation and off-beat patterns",
        ],
      },
      {
        title: "Time signatures and form",
        items: [
          "Understanding 4/4, 3/4 and 6/8 time",
          "Basic song structure awareness (verse / chorus)",
        ],
      },
    ],
  },
  3: {
    id: 3,
    title: "Level 3 — Advanced 1 (Musical Expansion)",
    objective:
      "To broaden stylistic awareness, improve control of rudiments and apply more complex grooves and fills.",
    assessmentFocus: [
      "Independence",
      "Subdivision accuracy",
      "Musical control",
      "Consistency",
    ],
    examFormat: {
      title: "Exam Format (Level 3)",
      totalMarks: 100,
      components: [
        { criteria: "Practical Performance", marks: 70 },
        { criteria: "Theory", marks: 30 },
      ],
      notes: "Formal theory assessment begins at this level (written or oral).",
    },
    practicalSections: [
      {
        title: "Grooves and styles",
        items: [
          "More advanced rock grooves with syncopated bass drum",
          "Introduction to basic funk or shuffle patterns",
          "Playing consistent hi-hat patterns with dynamics",
        ],
      },
      {
        title: "Rudiments and technical work",
        items: [
          "Single paradiddle and paradiddle-diddle at higher tempos",
          "Flams and drag rudiments in simple applications",
        ],
      },
      {
        title: "Fills and transitions",
        items: [
          "Two-bar fills combining snare, toms and bass drum",
          "Dynamic build-ups and controlled endings",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Two to three pieces in contrasting styles, with clear dynamic shaping",
          "Short solo or break section incorporating rudiments",
        ],
      },
    ],
    theorySections: [
      {
        title: "Rhythmic development",
        items: [
          "Triplets and their use in grooves and fills",
          "6/8 and compound time subdivisions",
        ],
      },
      {
        title: "Notation and structure",
        items: [
          "Reading drum charts with basic repeats",
          "Understanding form markings (D.C., D.S., codas in simple contexts)",
        ],
      },
    ],
  },
  4: {
    id: 4,
    title: "Level 4 — Advanced 2 (Artistic Control)",
    objective:
      "To achieve greater artistic control, stylistic nuance and reliable performance in extended pieces.",
    assessmentFocus: [
      "Coordination complexity",
      "Stylistic accuracy",
      "Musical expression",
      "Endurance",
    ],
    examFormat: {
      title: "Exam Format (Level 4)",
      totalMarks: 100,
      components: [
        { criteria: "Practical Performance", marks: 70 },
        { criteria: "Theory", marks: 30 },
      ],
      notes: "Prepares the drummer for professional-style applications.",
    },
    practicalSections: [
      {
        title: "Advanced grooves and styles",
        items: [
          "Stylistically appropriate grooves in rock, funk and related genres",
          "Use of ghost notes and dynamic hi-hat work",
          "More complex coordination between limbs",
        ],
      },
      {
        title: "Rudiments and linear patterns",
        items: [
          "Application of multiple rudiments around the kit",
          "Linear phrasing exercises for hands and feet",
        ],
      },
      {
        title: "Fills and soloing",
        items: [
          "Four-bar fills and short solo passages",
          "Development of motifs within solos",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Three or more extended pieces with stylistic accuracy",
          "At least one piece requiring sustained dynamic control and endurance",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced rhythm",
        items: [
          "Understanding and counting more complex syncopations",
          "Subdivisions in compound and mixed meters",
        ],
      },
      {
        title: "Chart reading and form",
        items: [
          "Reading more detailed drum charts",
          "Recognising extended song forms and repeats",
        ],
      },
    ],
  },
  5: {
    id: 5,
    title: "Level 5 — Professional",
    objective:
      "To demonstrate professional-level drum kit performance, stylistic range and musical responsibility within an ensemble.",
    assessmentFocus: [
      "Professional consistency",
      "Musical maturity",
      "Endurance",
      "Stylistic authority",
    ],
    examFormat: {
      title: "Exam Format (Level 5)",
      totalMarks: 100,
      components: [
        { criteria: "Practical Performance", marks: 80 },
        { criteria: "Theory", marks: 20 },
      ],
      notes: "Certifies professional-level time control, stylistic authority, endurance, and musical leadership.",
    },
    practicalSections: [
      {
        title: "Technique and sound",
        items: [
          "Consistent tone and dynamic control across the kit",
          "Clean execution of a range of rudiments at professional tempos",
        ],
      },
      {
        title: "Grooves and styles",
        items: [
          "Proficient grooves in multiple styles (rock, funk, pop and related genres)",
          "Understanding of stylistic feel (swing, straight, shuffle, etc.)",
        ],
      },
      {
        title: "Fills, solos and ensemble work",
        items: [
          "Musically appropriate fills and trading sections",
          "Short drum solo demonstrating control and creativity",
          "Reliable time-keeping and interaction in ensemble or backing-track contexts",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced rhythm and meter",
        items: [
          "Awareness of odd meters (e.g. 5/4, 7/8)",
          "Use of subdivision and counting strategies",
        ],
      },
      {
        title: "Chart interpretation",
        items: [
          "Interpreting lead sheets, rhythm charts and basic arrangements",
        ],
      },
    ],
  },
  6: {
    id: 6,
    title: "Level 6 — Mastery (Artist / Educator / Performer Level)",
    objective:
      "To certify artistic authority, musical leadership and the capacity to contribute to and guide ensembles at a high level.",
    assessmentFocus: [
      "Artistic authority",
      "Technical mastery",
      "Leadership",
      "Longevity and professionalism",
    ],
    examFormat: {
      title: "Exam Format (Level 6)",
      totalMarks: 100,
      components: [
        { criteria: "Practical Performance", marks: 80 },
        { criteria: "Theory / Viva / Research", marks: 20 },
      ],
      notes: "Successful candidates receive 'Global Certified Professional Drummer – Mastery Level'. This certifies readiness for professional performance, recording sessions, teaching and mentorship.",
    },
    practicalSections: [
      {
        title: "Recital and performance",
        items: [
          "A recital or recording portfolio of approximately 30–45 minutes of contrasting material",
          "Inclusion of original or co-written material where appropriate",
          "Demonstration of leadership within an ensemble or studio context",
        ],
      },
    ],
    theorySections: [
      {
        title: "Professional practice and pedagogy",
        items: [
          "Awareness of practice strategies, rehearsal techniques and stagecraft",
          "Introduction to teaching approaches and curriculum planning for drums",
        ],
      },
    ],
  },
};
